(function () {
  const PRINT_FEE_HANDLES = {
    one: 'one-colour-printing-fee',
    two: 'two-colour-printing-fee'
  };

  const SETUP_FEE_HANDLES = {
    one: 'one-colour-setup-fee',
    two: 'two-colour-setup-fee'
  };

  let syncInProgress = false;
  const productCache = {};

  function normalizeLogoType(value) {
    const normalized = String(value || '').toLowerCase();

    if (normalized.includes('one')) return 'one';

    if (
      normalized.includes('two') ||
      normalized.includes('multi')
    ) {
      return 'two';
    }

    return null;
  }

  function getPrintMultiplier(location) {
    return location === 'Product + Box' ? 2 : 1;
  }

  function getItemLogoType(item) {
    const properties = item.properties || {};

    return (
      properties['Logo Printing'] ||
      properties['Logo Colors'] ||
      ''
    );
  }

  function getItemPrintLocation(item) {
    const properties = item.properties || {};

    return (
      properties['Print On'] ||
      properties['Print on'] ||
      properties['Print Location'] ||
      ''
    );
  }

  function isFeeHandle(handle) {
    return (
      handle === PRINT_FEE_HANDLES.one ||
      handle === PRINT_FEE_HANDLES.two ||
      handle === SETUP_FEE_HANDLES.one ||
      handle === SETUP_FEE_HANDLES.two
    );
  }

  async function fetchCurrentCart() {
    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(`${root}cart.js`, {
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to load the current cart.');
    }

    return response.json();
  }

  async function fetchProduct(handle) {
    if (productCache[handle]) {
      return productCache[handle];
    }

    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(
      `${root}products/${handle}.js`,
      {
        headers: {
          Accept: 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Unable to load fee product: ${handle}`);
    }

    const product = await response.json();
    productCache[handle] = product;

    return product;
  }

  function calculateRequiredFees(cart) {
    const totals = {
      one: 0,
      two: 0
    };

    const existing = {
      print: {
        one: [],
        two: []
      },
      setup: {
        one: [],
        two: []
      }
    };

    cart.items.forEach(function (item) {
      if (item.handle === PRINT_FEE_HANDLES.one) {
        existing.print.one.push(item);
        return;
      }

      if (item.handle === PRINT_FEE_HANDLES.two) {
        existing.print.two.push(item);
        return;
      }

      if (item.handle === SETUP_FEE_HANDLES.one) {
        existing.setup.one.push(item);
        return;
      }

      if (item.handle === SETUP_FEE_HANDLES.two) {
        existing.setup.two.push(item);
        return;
      }

      if (isFeeHandle(item.handle)) return;

      const properties = item.properties || {};
      const logoType = getItemLogoType(item);
      const printLocation = getItemPrintLocation(item);

      if (!logoType || !printLocation) return;

      const typeKey = normalizeLogoType(logoType);
      if (!typeKey) return;

      const printUnits = Number(properties['_Print Units'] || 0);

      totals[typeKey] +=
      Number(item.quantity || 0) *
      printUnits;
    });

    return {
      totals,
      existing
    };
  }

  async function updateExistingFees(required) {
    const updates = {};

    for (const typeKey of ['one', 'two']) {
      const desiredPrintQuantity = required.totals[typeKey];
      const printItems = required.existing.print[typeKey];
      const setupItems = required.existing.setup[typeKey];

      /*
       * Keep one printing-fee line and remove any duplicates.
       */
      printItems.forEach(function (item, index) {
        if (index === 0 && desiredPrintQuantity > 0) {
          if (item.quantity !== desiredPrintQuantity) {
            updates[item.key] = desiredPrintQuantity;
          }
        } else {
          updates[item.key] = 0;
        }
      });

      /*
       * Keep one setup-fee line at quantity 1 while printed
       * products remain. Remove duplicates or orphaned setup fees.
       */
      setupItems.forEach(function (item, index) {
        if (index === 0 && desiredPrintQuantity > 0) {
          if (item.quantity !== 1) {
            updates[item.key] = 1;
          }
        } else {
          updates[item.key] = 0;
        }
      });
    }

    if (Object.keys(updates).length === 0) {
      return false;
    }

    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        updates
      })
    });

    if (!response.ok) {
      throw new Error('Unable to update printing fees.');
    }

    return true;
  }

  async function addMissingFees(required) {
    const itemsToAdd = [];

    for (const typeKey of ['one', 'two']) {
      const desiredPrintQuantity = required.totals[typeKey];

      if (desiredPrintQuantity <= 0) continue;

      if (required.existing.print[typeKey].length === 0) {
        const printProduct = await fetchProduct(
          PRINT_FEE_HANDLES[typeKey]
        );

        const printVariant = printProduct.variants?.[0];

        if (!printVariant) {
          throw new Error(
            `No variant found for ${PRINT_FEE_HANDLES[typeKey]}.`
          );
        }

        itemsToAdd.push({
          id: printVariant.id,
          quantity: desiredPrintQuantity
        });
      }

      if (required.existing.setup[typeKey].length === 0) {
        const setupProduct = await fetchProduct(
          SETUP_FEE_HANDLES[typeKey]
        );

        const setupVariant = setupProduct.variants?.[0];

        if (!setupVariant) {
          throw new Error(
            `No variant found for ${SETUP_FEE_HANDLES[typeKey]}.`
          );
        }

        itemsToAdd.push({
          id: setupVariant.id,
          quantity: 1
        });
      }
    }

    if (itemsToAdd.length === 0) {
      return false;
    }

    const root = window.Shopify?.routes?.root || '/';

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({
        items: itemsToAdd
      })
    });

    if (!response.ok) {
      throw new Error('Unable to add the required printing fees.');
    }

    return true;
  }

  window.syncPrivateLabelCart = async function () {
    if (syncInProgress) return false;

    syncInProgress = true;

    try {
      /*
       * Allow Shopify's original cart quantity/removal request
       * to finish before reading the cart.
       */
      await new Promise(function (resolve) {
        setTimeout(resolve, 200);
      });

      const cart = await fetchCurrentCart();
      const required = calculateRequiredFees(cart);

      const existingFeesChanged =
        await updateExistingFees(required);

      /*
       * Fetch the cart again after removals/quantity corrections,
       * so missing-fee detection uses the newest cart state.
       */
      const refreshedCart = existingFeesChanged
        ? await fetchCurrentCart()
        : cart;

      const refreshedRequired =
        calculateRequiredFees(refreshedCart);

      const missingFeesAdded =
        await addMissingFees(refreshedRequired);

      return existingFeesChanged || missingFeesAdded;
    } catch (error) {
      console.error(
        'Private-label cart synchronization failed:',
        error
      );

      const errorElement =
        document.getElementById('cart-errors') ||
        document.getElementById('CartDrawer-CartErrors');

      if (errorElement) {
        errorElement.textContent =
          'The printing fees could not be updated. Please refresh and try again.';
      }

      return false;
    } finally {
      syncInProgress = false;
    }
  };
})();